import { useMemo } from 'react';
import { useSMLTheme } from './useSMLTheme';

export interface ChartTokens {
  grid: string;
  zero: string;
  cursor: string;
  axis: string;
  label: string;
  dotCore: string;
  ringTrack: string;
  brand: string;
  brandLift: string;
  pos: string;
  neg: string;
  flat: string;
  line: string;
  tooltipBg: string;
  tooltipTx: string;
  tx0: string;
  tx1: string;
  tx2: string;
}

const FALLBACK: ChartTokens = {
  grid: 'rgba(255,255,255,0.05)',
  zero: 'rgba(255,255,255,0.18)',
  cursor: 'rgba(255,255,255,0.22)',
  axis: '#807a92',
  label: '#565067',
  dotCore: '#0a0911',
  ringTrack: 'rgba(255,255,255,0.08)',
  brand: '#6417fb',
  brandLift: '#8b4dff',
  pos: '#34d399',
  neg: '#fb5a78',
  flat: '#6a6480',
  line: 'rgba(255,255,255,0.06)',
  tooltipBg: '#060509',
  tooltipTx: '#f5f3fa',
  tx0: '#f5f3fa',
  tx1: '#b7b2c6',
  tx2: '#807a92',
};

/**
 * Resolves the design-language chart-ink CSS custom properties to concrete
 * color strings for recharts (which needs JS values, not `var()`). Recomputes
 * whenever the theme toggles so charts re-render with the active palette.
 */
export function useChartTokens(): ChartTokens {
  const { theme } = useSMLTheme();

  return useMemo(() => {
    if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') {
      return FALLBACK;
    }
    const root = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string): string => {
      const value = root.getPropertyValue(name).trim();
      return value === '' ? fallback : value;
    };
    return {
      grid: read('--chart-grid', FALLBACK.grid),
      zero: read('--chart-zero', FALLBACK.zero),
      cursor: read('--chart-cursor', FALLBACK.cursor),
      axis: read('--chart-axis', FALLBACK.axis),
      label: read('--chart-label', FALLBACK.label),
      dotCore: read('--chart-dot-core', FALLBACK.dotCore),
      ringTrack: read('--ring-track', FALLBACK.ringTrack),
      brand: read('--brand-violet', FALLBACK.brand),
      brandLift: read('--brand-violet-lift', FALLBACK.brandLift),
      pos: read('--pos', FALLBACK.pos),
      neg: read('--neg', FALLBACK.neg),
      flat: read('--flat', FALLBACK.flat),
      line: read('--line', FALLBACK.line),
      tooltipBg: read('--tooltip-bg', FALLBACK.tooltipBg),
      tooltipTx: read('--tooltip-tx', FALLBACK.tooltipTx),
      tx0: read('--tx-0', FALLBACK.tx0),
      tx1: read('--tx-1', FALLBACK.tx1),
      tx2: read('--tx-2', FALLBACK.tx2),
    };
    // theme drives the recompute even though it isn't referenced directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);
}
